import { useTranslation } from "react-i18next";
import TeeBadge, { type BadgeType } from "@/components/ui/tee-badge";
import { TeeLoader } from "@/components/ui/tee-loader";
import { TeeButton } from "@/components/ui/tee-button";
import { InboxEmptyState } from "@/pages/inbox/components/InboxEmptyState";
import { useSupportTickets } from "@/hooks/useSupportTickets";
import type { TicketStatus, TicketRequestType } from "@/types/support-ticket";
import type { TicketFilterValues } from "./TicketFilters";

export const TICKET_PAGE_SIZE = 25;

export const REQUEST_TYPE_LABEL_KEY: Record<TicketRequestType, string> = {
  issue: "intake.request_type_issue",
  feature_request: "intake.request_type_feature",
  feedback: "intake.request_type_feedback",
};

export const TICKET_BADGE_STATUS: Record<TicketStatus, string> = {
  open: "pending",
  in_progress: "active",
  closed: "inactive",
};

export const TICKET_PILL_TYPE: Record<TicketStatus, BadgeType> = {
  open: "amber",
  in_progress: "green",
  closed: "grey",
};

interface TicketListPaneProps {
  filters: TicketFilterValues;
  page: number;
  selectedId?: string;
  onSelect: (id: string) => void;
  onPageChange: (page: number) => void;
}

export function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.max(0, Math.round(diffMs / 60000));
  if (min < 1) return "now";
  if (min < 60) return `${min}m ago`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function TicketListPane({
  filters,
  page,
  selectedId,
  onSelect,
  onPageChange,
}: TicketListPaneProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useSupportTickets({
    status: filters.status,
    assignee: filters.assignee || undefined,
    locationId: filters.locationId || undefined,
    search: filters.search || undefined,
    page,
    limit: TICKET_PAGE_SIZE,
  });

  if (isError) {
    return (
      <InboxEmptyState
        tone="error"
        title={t("supportTickets.list.error")}
        actionLabel={t("common.retry")}
        onAction={() => void refetch()}
      />
    );
  }

  const rows = data?.data ?? [];
  const hasFilters =
    filters.status !== "all" ||
    !!filters.assignee ||
    !!filters.locationId ||
    !!filters.search;

  if (!isLoading && rows.length === 0) {
    return (
      <InboxEmptyState
        title={
          hasFilters
            ? t("supportTickets.list.emptyTitle")
            : t("supportTickets.list.noneBody")
        }
        body={hasFilters ? t("supportTickets.list.emptyBody") : undefined}
      />
    );
  }

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / TICKET_PAGE_SIZE));

  if (isLoading && rows.length === 0) {
    return (
      <div className="flex justify-center p-8">
        <TeeLoader size="sm" />
      </div>
    );
  }

  return (
    <div className="flex flex-col max-lg:m-4 max-lg:overflow-hidden max-lg:rounded-[12px] max-lg:border max-lg:border-[var(--border-default)]">
      {rows.map((row) => {
        const selected = row.id === selectedId;
        const describedBy = `ticket-card-${row.id}`;
        const meta = [row.requesterName, row.locationName]
          .filter(Boolean)
          .join(" · ");
        return (
          <div
            key={row.id}
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            aria-label={row.ticketNumber}
            aria-describedby={describedBy}
            onClick={() => onSelect(row.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(row.id);
              }
            }}
            className={`flex w-full cursor-pointer flex-col gap-1.5 border-b border-[var(--border-default)] p-4 text-left transition-colors hover:bg-[var(--background-card-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--border-active)] ${
              selected
                ? "bg-[var(--background-card-elevated)] shadow-[inset_3px_0_0_var(--color-forest)]"
                : "bg-[var(--background-card)]"
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-bold tracking-[.04em] text-[var(--color-forest)]">
                {row.ticketNumber}
              </span>
              {row.isNew && (
                <>
                  <span
                    aria-hidden="true"
                    className="h-2 w-2 rounded-full bg-[var(--color-gold-500)]"
                  />
                  <span className="sr-only">
                    {t("supportTickets.list.new")}
                  </span>
                </>
              )}
              {row.source === "form" && (
                <TeeBadge
                  size="s"
                  type="grey-outline"
                  label={t("supportTickets.list.formOrigin")}
                />
              )}
              {row.awaitingHqReply && (
                <TeeBadge
                  size="s"
                  type="amber"
                  label={t("supportTickets.list.newReply")}
                />
              )}
              {row.linearIssue && (
                <TeeBadge
                  size="s"
                  type="grey-outline"
                  label={row.linearIssue.identifier}
                />
              )}
              <span className="ml-auto text-[12px] text-[var(--text-content-sub-title)]">
                {relativeTime(row.createdAt)}
              </span>
            </div>
            <div id={describedBy} className="flex flex-col gap-1.5">
              <span className="line-clamp-2 text-[15px] font-semibold text-[var(--text-content-title)] max-md:text-[16px]">
                {row.summary}
              </span>
              <div className="flex items-center gap-2 text-[13px] text-[var(--text-content-sub-title)]">
                <TeeBadge
                  size="s"
                  type={TICKET_PILL_TYPE[row.status]}
                  label={t(`supportTickets.filters.${row.status}`)}
                />
                {row.requestType && (
                  <TeeBadge
                    size="s"
                    type="white"
                    label={t(REQUEST_TYPE_LABEL_KEY[row.requestType])}
                  />
                )}
                {row.severity === "critical" && (
                  <TeeBadge
                    size="s"
                    type="red"
                    label={t("supportTickets.list.critical")}
                  />
                )}
                <span className="min-w-0 truncate">{meta}</span>
              </div>
            </div>
          </div>
        );
      })}
      <div className="flex items-center justify-between gap-2 border-t border-[var(--border-default)] px-4 py-3 text-[13px] text-[var(--text-content-sub-title)]">
        <span>
          {t("supportTickets.list.showing", { shown: rows.length, total })}
        </span>
        {totalPages > 1 && (
          <div className="flex gap-1">
            <TeeButton
              variant="ghost"
              size="s"
              text={t("supportTickets.list.prev")}
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            />
            <TeeButton
              variant="ghost"
              size="s"
              text={t("supportTickets.list.next")}
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default TicketListPane;
